import { useEffect, useRef, useState } from "react";
import { Check, Copy, Download, KeyRound, X } from "lucide-react";
import { api, guttenbergApiOrigin } from "./api";

export function GuttenbergCopilot({ projectId, projectTitle, prominent = false }) {
  const [open, setOpen] = useState(false);
  const [access, setAccess] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [days, setDays] = useState(7);
  const [projectSources, setProjectSources] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const closeRef = useRef(null);
  const connectionFilename = `guttenberg-connexion-${projectId}.json`;
  const prompt = `Utilise $guttenberg-report pour créer une version complète de mon rapport dans Guttenberg. `
    + `Le fichier de connexion « ${connectionFilename} » se trouve dans mon dossier Téléchargements ; retrouve ce fichier précis et connecte le dossier sans afficher sa clé. `
    + `Reprends les sources, mes réponses, mes choix d'images et les travaux déjà vérifiés. `
    + `Consulte aussi les données du projet lié lorsque l'accès est autorisé : journaux et images, factures, notes et fichiers Drive peuvent éclairer l'analyse. `
    + `Utilise la recherche Knowledge si utile et télécharge les données originales pour réaliser tes analyses et tests numériques sur mon PC. `
    + `Suis les demandes du brief, conserve les preuves des chiffres et sauvegarde ton avancement. `
    + `Rédige dans un brouillon, vérifie le PDF natif, puis publie la version relue dans Guttenberg. `
    + `Ne lance pas le moteur de génération intégré.`;
  const load = async () => {
    const result = await api.copilotAccess(projectId);
    setAccess(result.items || []);
    setSessions(result.sessions || []);
  };
  useEffect(() => {
    if (!open) return undefined;
    closeRef.current?.focus();
    load().catch((e) => setError(e.message));
    const key = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
    // A refresh is explicit; do not repeatedly poll a credential-management panel.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);
  useEffect(() => {
    setOpen(false); setCreated(null); setAccess([]); setSessions([]);
    setDownloaded(false); setCopied(false); setError("");
    setProjectSources(false);
  }, [projectId]);

  const create = async () => {
    setBusy(true); setError("");
    try {
      setCreated(await api.createCopilotAccess(projectId, { label: "Codex", days: Number(days),
        ...(projectSources ? { knowledge_scope: "linked_project", project_read_scope: "linked_project" } : {}) }));
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const revoke = async (key) => {
    setBusy(true); setError("");
    try {
      await api.revokeCopilotAccess(projectId, key);
      if (created?.id === key) setCreated(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const download = () => {
    const connection = { format: "guttenberg-copilot-connection-v1", api_url: guttenbergApiOrigin(),
      project_id: projectId, title: projectTitle, token: created.token, expires_at: created.expires_at };
    const url = URL.createObjectURL(new Blob([JSON.stringify(connection, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url; a.download = connectionFilename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setDownloaded(true);
  };
  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopied(true); setError("");
    } catch {
      setError("La copie n'a pas abouti. Tu peux sélectionner le message ci-dessous et le copier.");
    }
  };
  const openPanel = () => {
    setCreated(null); setError(""); setDownloaded(false); setCopied(false); setOpen(true);
  };
  return <>
    {prominent ? <section className="copilot-launcher" aria-labelledby="copilot-launcher-title">
      <div><h2 id="copilot-launcher-title">Rédiger avec Codex</h2>
        <p>Prépare tes sources et ton brief ici. Codex analyse, rédige et relit le rapport ; tu retrouves la version éditable et les exports dans Guttenberg.</p>
      </div>
      <button className="button" type="button" onClick={openPanel}><KeyRound size={17}/> Connecter Codex</button>
    </section> : <button className="button secondary" type="button" onClick={openPanel}>
      <KeyRound size={17}/> Codex
    </button>}
    {open && <div className="copilot-backdrop" onClick={() => setOpen(false)}>
      <section className="copilot-dialog" role="dialog" aria-modal="true" aria-labelledby="copilot-title" onClick={(e) => e.stopPropagation()}>
        <div className="copilot-heading"><h2 id="copilot-title">Travailler sur ce rapport avec Codex</h2>
          <button ref={closeRef} className="button secondary" type="button" aria-label="Fermer" onClick={() => setOpen(false)}><X size={18}/></button></div>
        <p>Codex accède au brief, aux sources et aux images de ce dossier. Il calcule et rédige dans un brouillon indépendant, puis publie une révision éditable avec un point de restauration.</p>
        <p className="muted">La connexion fonctionne dans une conversation Codex qui dispose des outils Guttenberg. Une nouvelle conversation n'est nécessaire qu'après la première installation ou une mise à jour de ces outils.</p>
        <ol className="copilot-steps">
          <li>Prépare les sources, le brief et tes choix d'images dans ce dossier. Tu peux reprendre un rapport déjà commencé.</li>
          <li>Crée un accès et télécharge le fichier de connexion ci-dessous.</li>
          <li>Envoie à Codex le message proposé après téléchargement. Il retrouve le fichier sur ton poste et ouvre ce rapport.</li>
        </ol>
        <details className="copilot-install"><summary>Les outils Guttenberg ne sont pas encore installés dans Codex ?</summary>
          <p>Depuis le dépôt Deepflow, installer l'intégration une seule fois, puis ouvrir une nouvelle conversation Codex :</p>
          <pre>python -m pip install -r integrations/guttenberg_mcp/requirements.txt{"\n"}python integrations/guttenberg_mcp/install.py</pre>
          <p>Ce parcours utilise Codex sur ton ordinateur. Un lien vers le site Guttenberg, seul, ne donne pas accès au rapport.</p>
        </details>
        <div className="copilot-actions"><label>Validité <select value={days} onChange={(e) => setDays(e.target.value)} disabled={busy}>
          <option value={1}>1 jour</option><option value={7}>7 jours</option><option value={30}>30 jours</option>
        </select></label><button className="button" type="button" onClick={create} disabled={busy || Boolean(created)}>Créer un accès à ce rapport</button></div>
        <label><input type="checkbox" checked={projectSources} disabled={busy || Boolean(created)}
          onChange={(e) => setProjectSources(e.target.checked)}/>{" "}
          Autoriser aussi la lecture des données du projet Deepflow lié</label>
        <p className="muted">Journaux et images, factures, notes, objectifs, matériel, temps passés et documents Drive, avec recherche dans les contenus indexés. Le Drive utilise ta connexion Google dans Deepflow. Ces données restent en lecture seule ; les connexions existantes conservent leur périmètre.</p>
        {created && <div className="copilot-secret">
          <button className="button" type="button" onClick={download}><Download size={17}/> Télécharger la connexion</button>
          <p>Garde le fichier sur ton poste. Il contient une clé limitée au périmètre choisi : donne à Codex son emplacement, sans coller son contenu.</p>
          {downloaded && <div className="copilot-handoff">
            <label htmlFor="copilot-prompt">Message à envoyer dans Codex</label>
            <textarea id="copilot-prompt" value={prompt} readOnly rows={7} onFocus={(event) => event.target.select()}/>
            <button className="button secondary" type="button" onClick={copyPrompt}>
              {copied ? <Check size={17}/> : <Copy size={17}/>} {copied ? "Message copié" : "Copier le message"}
            </button>
            <p>Si tu as enregistré le fichier ailleurs ou si son nom a changé, remplace l'emplacement ou le nom dans le message. La consommation IA se fait dans Codex et n'apparaît pas dans les coûts de génération Guttenberg.</p>
          </div>}
        </div>}
        {error && <p role="alert" className="copilot-error">{error}</p>}
        <h3>Accès délivrés</h3>
        {access.length === 0 ? <p className="muted">Aucun accès.</p> : <ul className="copilot-list">{access.map((item) => <li key={item.id}>
          <span>{item.label} · {item.project_read_scope === "linked_project" ? "Rapport + lecture du projet" : item.knowledge_scope === "linked_project" ? "Rapport + recherche indexée" : "Rapport"} · {item.revoked ? "Révoqué" : `Expire le ${new Date(item.expires_at).toLocaleString()}`}</span>
          {!item.revoked && <button className="button secondary" disabled={busy} type="button" onClick={() => revoke(item.id)}>Révoquer</button>}
        </li>)}</ul>}
        {sessions.length > 0 && <><h3>Travail enregistré</h3><ul className="copilot-list">{sessions.map((s) => <li key={s.id}>
          <div><span>{s.label} · {s.status === "published" ? "Publié dans le rapport" : `Brouillon, révision ${s.revision}`}</span>
            {s.preview?.status === "ready" && <p className="muted">Aperçu PDF disponible dans le brouillon.</p>}
            {s.checkpoint?.next_action && typeof s.checkpoint.next_action === "string" && <p className="muted">Prochaine étape : {s.checkpoint.next_action}</p>}
            <p className="muted">Pour reprendre dans Codex : « Reprends le brouillon {s.id} de ce rapport. »</p>
          </div>
          <span className="muted">{new Date(s.updated_at).toLocaleString()}</span>
        </li>)}</ul></>}
      </section>
    </div>}
  </>;
}
